import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import Icon from '../../../components/AppIcon';

const TIER_MULTIPLIERS = {
  bronze: { label: 'Bronze', multiplier: 1.0, color: '#CD7F32', nextTier: 'Silver', boost: 1.5 },
  silver: { label: 'Silver', multiplier: 1.5, color: '#94A3B8', nextTier: 'Gold', boost: 1.8 },
  gold: { label: 'Gold', multiplier: 1.8, color: '#F59E0B', nextTier: 'Platinum', boost: 2.2 },
  platinum: { label: 'Platinum', multiplier: 2.2, color: '#E2E8F0', nextTier: 'Diamond', boost: 2.8 },
  diamond: { label: 'Diamond', multiplier: 2.8, color: '#14B8A6', nextTier: null, boost: 2.8 },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
      <p className="text-xs font-semibold text-foreground mb-1">{label}</p>
      {payload?.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: entry?.fill }} />
          <span className="text-xs text-muted-foreground">{entry?.name}:</span>
          <span className="text-xs font-bold text-foreground">${parseFloat(entry?.value || 0)?.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

const EarningForecasts = ({ tier, avgMonthlyEarning, activeCampaignCount, loading }) => {
  const tierKey = tier?.toLowerCase() || 'bronze';
  const tierInfo = TIER_MULTIPLIERS?.[tierKey] || TIER_MULTIPLIERS?.bronze;
  const nextTierInfo = tierInfo?.nextTier ? TIER_MULTIPLIERS?.[tierInfo?.nextTier?.toLowerCase()] : null;

  const baseMonthly = avgMonthlyEarning || 0;
  const campaignBoost = activeCampaignCount > 0 ? activeCampaignCount * 2.5 : 0;

  const scenarios = [
    {
      label: 'Conservative',
      value: parseFloat((baseMonthly * 0.8 + campaignBoost)?.toFixed(2)),
      color: '#64748B',
      desc: 'Fewer campaigns, current tier',
    },
    {
      label: 'Current Pace',
      value: parseFloat((baseMonthly + campaignBoost)?.toFixed(2)),
      color: '#14B8A6',
      desc: 'Maintain current activity',
    },
    {
      label: 'Optimistic',
      value: parseFloat((baseMonthly * 1.4 + campaignBoost * 1.5)?.toFixed(2)),
      color: '#3B82F6',
      desc: 'More campaigns, higher engagement',
    },
    {
      label: 'Next Tier',
      value: nextTierInfo
        ? parseFloat((baseMonthly * nextTierInfo?.multiplier + campaignBoost * 1.5)?.toFixed(2))
        : parseFloat((baseMonthly * tierInfo?.multiplier + campaignBoost * 2)?.toFixed(2)),
      color: '#F59E0B',
      desc: nextTierInfo ? `If you reach ${nextTierInfo?.label} tier` : 'Max tier achieved',
    },
  ];

  const currentMonthForecast = scenarios?.[1]?.value;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center">
              <Icon name="Sparkles" size={18} className="text-violet-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground font-heading">Earning Forecasts</h3>
              <p className="text-xs text-muted-foreground">Based on {tierInfo?.label} tier & activity</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-foreground data-text">${currentMonthForecast?.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">projected this month</p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="h-44 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Tier Badge */}
            <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-muted/40">
              <div
                className="w-3 h-3 rounded-full"
                style={{ background: tierInfo?.color }}
              />
              <span className="text-xs font-medium text-foreground">{tierInfo?.label} Tier</span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{activeCampaignCount || 0} active campaigns</span>
              {nextTierInfo && (
                <>
                  <span className="text-xs text-muted-foreground ml-auto">Next: {nextTierInfo?.label}</span>
                  <Icon name="ArrowRight" size={12} className="text-muted-foreground" />
                </>
              )}
            </div>

            {/* Bar Chart */}
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={scenarios} margin={{ top: 5, right: 5, left: -20, bottom: 0 }} barSize={32}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.5)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#94A3B8', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#94A3B8', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => `$${v}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Forecast" radius={[4, 4, 0, 0]}>
                  {scenarios?.map((entry, index) => (
                    <Cell key={index} fill={entry?.color} fillOpacity={index === 1 ? 1 : 0.65} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Scenario Descriptions */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {scenarios?.map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: s?.color }} />
                  <div>
                    <p className="text-xs font-medium text-foreground">{s?.label}</p>
                    <p className="text-xs text-muted-foreground">{s?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EarningForecasts;
